import { useState, useCallback, useRef } from 'react';
import { auth$ } from '@/stores/auth$';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

/**
 * Hook for calling AI actions edge function.
 * Supports: suggest-title, classify-note
 */
export function useAIAction() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const abortRef = useRef(null);

  const clearResult = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  const abort = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setLoading(false);
  }, []);

  /**
   * Suggest titles for a note based on its content.
   * @param {string} noteId
   * @returns {Promise<string[]|null>} Array of suggested titles, or null on error
   */
  const suggestTitle = useCallback(async (noteId) => {
    // Abort any in-flight request
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const session = auth$.session.get();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/ai-actions/suggest-title`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ noteId }),
          signal: controller.signal,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Request failed (${response.status})`);
      }

      setResult(data.titles);
      return data.titles;
    } catch (e) {
      if (e.name === 'AbortError') return null;
      const msg = e.message || 'Failed to suggest titles';
      setError(msg);
      console.error('[useAIAction] suggestTitle error:', msg);
      return null;
    } finally {
      if (abortRef.current === controller) {
        setLoading(false);
      }
    }
  }, []);

  return {
    suggestTitle,
    loading,
    error,
    result,
    clearResult,
    abort,
  };
}

/**
 * Classify a note with smart tags. Standalone function (not a hook).
 * Manages its own lifecycle — fire-and-forget with callback.
 * @param {string} noteId
 * @param {function} onSuccess - called with smartTags array on success
 */
export async function classifyNote(noteId, onSuccess) {
  try {
    const session = auth$.session.get();
    if (!session?.access_token) return;

    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/ai-actions/classify-note`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ noteId }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      console.error('[classifyNote] error:', data.error);
      return;
    }

    if (data.smartTags && onSuccess) {
      onSuccess(data.smartTags);
    }
  } catch (e) {
    console.error('[classifyNote] error:', e.message);
  }
}
